import React, { lazy, Suspense } from 'react';
import { render, RenderResult } from '@testing-library/react';
import { Navigate, useRoutes, MemoryRouter } from 'react-router-dom';

import { DefaultLayout } from 'src/layouts';
import { PATH_AFTER_LOGIN } from 'src/config-global';

import { SplashScreen } from 'src/components/loading-screen';
import { AuthGuard, GuestGuard } from 'src/auth/guard';
import { useAuthStore } from 'src/auth/auth.store';

import { authRoutes } from './auth';
import { errorRoutes } from './error';

// PAGE_IMPORTS
const WelcomePage = lazy(() => import('src/modules/welcome/welcome'));
const AppHomePage = lazy(() => import('src/modules/app/components/home'));
const BenefitDetailsPage = lazy(() => import('src/modules/app/components/benefit-details'));
const RedeemSuccessPage = lazy(() => import('src/modules/app/components/redeem-success'));
const OffersPage = lazy(() => import('src/modules/app/components/offers'));
const RedeemsPage = lazy(() => import('src/modules/app/components/redeems'));
const StatementPage = lazy(() => import('src/modules/app/components/statement'));
const ProfilePage = lazy(() => import('src/modules/profile/components/profile'));
const MyAccountPage = lazy(() => import('src/modules/profile/components/my-account'));
const TermsPage = lazy(() => import('src/modules/profile/components/terms'));
// TODO: operador vinculado desativado no lançamento Manas - reativar depois
// const SellerPage = lazy(() => import('src/modules/profile/components/seller'));

export const renderWithRouter = (ui: React.ReactElement): RenderResult => render(<MemoryRouter>{ui}</MemoryRouter>);

function RootRedirect() {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <SplashScreen />;
  }

  return <Navigate to={isAuthenticated ? '/app' : '/welcome'} replace />;
}

export default function Router() {
  return useRoutes([
    {
      path: '/',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            {/* Redirect root based on auth state */}
            <RootRedirect />
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/welcome',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <GuestGuard>
              <WelcomePage />
            </GuestGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/app',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <AuthGuard>
              <AppHomePage />
            </AuthGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/app/benefits/:id',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <AuthGuard>
              <BenefitDetailsPage />
            </AuthGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/app/benefits/:id/success',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <AuthGuard>
              <RedeemSuccessPage />
            </AuthGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/offers',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            {/* <AuthGuard> */}
            <OffersPage />
            {/* </AuthGuard> */}
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/app/redeems',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <AuthGuard>
              <RedeemsPage />
            </AuthGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/app/statement',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <AuthGuard>
              <StatementPage />
            </AuthGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/app/profile',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <AuthGuard>
              <ProfilePage />
            </AuthGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/app/profile/my-account',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <AuthGuard>
              <MyAccountPage />
            </AuthGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/app/profile/terms',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <AuthGuard>
              <TermsPage />
            </AuthGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    // TODO: operador vinculado desativado no lançamento Manas - reativar depois
    // {
    //   path: '/app/profile/seller',
    //   element: (
    //     <Suspense fallback={<SplashScreen />}>
    //       <DefaultLayout>
    //         <AuthGuard>
    //           <SellerPage />
    //         </AuthGuard>
    //       </DefaultLayout>
    //     </Suspense>
    //   ),
    // },
    ...authRoutes,

    // RELATION_PATH_PAGES

    { path: '*', element: <Navigate to="/404" replace /> },

    ...errorRoutes,
  ]);
}
